/**
 * Animated search hints.
 *
 * Cycles the search field's placeholder through real examples — a part number,
 * an OEM reference, a plain-language part name — so a shopper who doesn't know
 * what the box accepts can see it accept three different things.
 *
 * Enhancement only. The input ships with the first hint already set as its
 * placeholder, so with JS off it reads as a normal, sensible search box.
 *
 * The accessible name comes from the field's <label>, not the placeholder, so
 * swapping this text never changes what a screen reader announces the field as.
 * That is why this is safe to animate at all.
 */

const VZ_HINT_INTERVAL = 3400;

// Long enough for the fade in vz-header.css to finish before the text swaps.
// Shorter and the old hint is still visible when it changes, which reads as a
// glitch rather than a transition.
const VZ_HINT_FADE = 220;

function vzStartHints(input) {
  const hints = (input.dataset.vzHints || '')
    .split('|')
    .map((hint) => hint.trim())
    .filter(Boolean);

  if (hints.length < 2) return;

  let index = 0;
  let timer = null;

  const swap = () => {
    // Never fight the shopper: no cycling while the field is focused, while it
    // holds text, or while the tab is in the background burning cycles.
    if (document.hidden || document.activeElement === input || input.value !== '') {
      return;
    }

    input.classList.add('is-hint-swapping');

    window.setTimeout(() => {
      index = (index + 1) % hints.length;
      input.placeholder = hints[index];
      input.classList.remove('is-hint-swapping');
    }, VZ_HINT_FADE);
  };

  const start = () => {
    if (timer) return;
    timer = window.setInterval(swap, VZ_HINT_INTERVAL);
  };

  const stop = () => {
    window.clearInterval(timer);
    timer = null;
    input.classList.remove('is-hint-swapping');
  };

  input.addEventListener('focus', stop);
  input.addEventListener('blur', () => {
    if (input.value === '') start();
  });

  start();
}

function vzInitHints() {
  // Someone who has asked for less motion gets the first hint and nothing else.
  // The placeholder is already meaningful, so there is nothing to fall back to.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('[data-vz-hints]').forEach(vzStartHints);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', vzInitHints);
} else {
  vzInitHints();
}